import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Download, 
  Filter, 
  FileText, 
  BarChart3, 
  Calendar,
  Building,
  User,
  Search,
  RefreshCw,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { reportsAPI, ReportFilters, ReportData, Facility, HealthCareProvider } from '../../services/api';
import { SkeletonReports } from '../Skeleton/SkeletonLoader';

const ReportsContainer = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
  margin-bottom: 32px;
  flex-wrap: wrap;
  gap: 16px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1e40af;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FiltersCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;
`;

const FiltersHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  font-size: 18px;
  font-weight: 600;
  color: #374151;
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Select = styled.select`
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'success' }>`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: #3b82f6;
          color: white;
          &:hover {
            background: #2563eb;
          }
        `;
      case 'success':
        return `
          background: #10b981;
          color: white;
          &:hover {
            background: #059669;
          }
        `;
      default:
        return `
          background: #f3f4f6;
          color: #374151;
          border: 2px solid #e5e7eb;
          &:hover {
            background: #e5e7eb;
          }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const ReportCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;
`;

const ReportHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 16px;
`;

const ReportTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const SummaryCard = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  text-align: center;
`;

const SummaryValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #1e40af;
  margin-bottom: 4px;
`;

const SummaryLabel = styled.div`
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
`;

const BreakdownSection = styled.div`
  margin-bottom: 24px;
`;

const BreakdownTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BreakdownTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const TableHeader = styled.th`
  background: #f8fafc;
  padding: 12px 16px;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
`;

const TableCell = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: #374151;
  border-bottom: 1px solid #f3f4f6;
`;

const TableRow = styled.tr`
  &:hover {
    background: #f8fafc;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #6b7280;
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
`;

const SuccessMessage = styled.div`
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #16a34a;
  padding: 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: #6b7280;
`;

const Reports: React.FC = () => {
  const [filters, setFilters] = useState<ReportFilters>({
    reportType: 'summary'
  });
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [doctors, setDoctors] = useState<HealthCareProvider[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Load initial data
  useEffect(() => {
    loadFacilities();
    loadDoctors();
  }, []);

  const loadFacilities = async () => {
    try {
      const response = await reportsAPI.getFacilities();
      setFacilities(response.data);
    } catch (error) {
      console.error('Error loading facilities:', error);
    }
  };

  const loadDoctors = async () => {
    try {
      const response = await reportsAPI.getDoctors();
      setDoctors(response.data);
    } catch (error) {
      console.error('Error loading doctors:', error);
    }
  };

  const generateReport = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await reportsAPI.generateReport(filters);
      setReportData(response.data);
      setSuccess('Report generated successfully!');
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async (format: 'csv' | 'json' = 'csv') => {
    setLoading(true);
    setError(null);

    try {
      const response = await reportsAPI.downloadReport({
        ...filters,
        format
      });

      // Create and download file
      const blob = new Blob([response.data.content], { 
        type: response.data.contentType 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = response.data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccess(`Report downloaded successfully! (${response.data.recordCount} records)`);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to download report');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      reportType: 'summary'
    });
    setReportData(null);
    setError(null);
    setSuccess(null);
  };

  return (
    <ReportsContainer>
      <Header>
        <Title>
          <FileText size={32} />
          Reports
        </Title>
        <ButtonGroup>
          <Button 
            variant="primary" 
            onClick={generateReport}
            disabled={loading}
          >
            {loading ? <RefreshCw size={16} className="animate-spin" /> : <BarChart3 size={16} />}
            Generate Report
          </Button>
          <Button 
            variant="success" 
            onClick={() => downloadReport('csv')}
            disabled={loading || !reportData}
          >
            <Download size={16} />
            Download CSV
          </Button>
          <Button 
            variant="secondary" 
            onClick={() => downloadReport('json')}
            disabled={loading || !reportData}
          >
            <Download size={16} />
            Download JSON
          </Button>
        </ButtonGroup>
      </Header>

      {error && (
        <ErrorMessage>
          <AlertCircle size={20} />
          {error}
        </ErrorMessage>
      )}

      {success && (
        <SuccessMessage>
          <CheckCircle size={20} />
          {success}
        </SuccessMessage>
      )}

      <FiltersCard>
        <FiltersHeader>
          <Filter size={20} />
          Report Filters
        </FiltersHeader>
        
        <FiltersGrid>
          <FilterGroup>
            <Label>
              <Building size={16} />
              Facility
            </Label>
            <Select
              value={filters.facilityName || ''}
              onChange={(e) => setFilters({ ...filters, facilityName: e.target.value || undefined })}
            >
              <option value="">All Facilities</option>
              {facilities.map((facility) => (
                <option key={facility.id} value={facility.name}>
                  {facility.name} ({facility.type})
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>
              <User size={16} />
              Health Care Provider
            </Label>
            <Select
              value={filters.doctorName || ''}
              onChange={(e) => setFilters({ ...filters, doctorName: e.target.value || undefined })}
            >
              <option value="">All Health Care Providers</option>
              {doctors.map((doctor, index) => (
                <option key={index} value={doctor.name}>
                  {doctor.name}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>
              <Search size={16} />
              Patient Number
            </Label>
            <Input
              type="text"
              placeholder="Enter patient number or name"
              value={filters.patientNumber || ''}
              onChange={(e) => setFilters({ ...filters, patientNumber: e.target.value || undefined })}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>
              <Calendar size={16} />
              Start Date
            </Label>
            <Input
              type="date"
              value={filters.startDate || ''}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value || undefined })}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>
              <Calendar size={16} />
              End Date
            </Label>
            <Input
              type="date"
              value={filters.endDate || ''}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value || undefined })}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>
              <FileText size={16} />
              Report Type
            </Label>
            <Select
              value={filters.reportType || 'summary'}
              onChange={(e) => setFilters({ ...filters, reportType: e.target.value as 'summary' | 'detailed' })}
            >
              <option value="summary">Summary Report</option>
              <option value="detailed">Detailed Report</option>
            </Select>
          </FilterGroup>
        </FiltersGrid>

        <ButtonGroup>
          <Button variant="secondary" onClick={clearFilters}>
            Clear Filters
          </Button>
        </ButtonGroup>
      </FiltersCard>

      {loading && <SkeletonReports />}

      {reportData && !loading && (
        <ReportCard>
          <ReportHeader>
            <ReportTitle>
              <BarChart3 size={20} />
              {reportData.type === 'summary' ? 'Summary Report' : 'Detailed Report'}
            </ReportTitle>
            <div style={{ color: '#6b7280', fontSize: '14px' }}>
              {reportData.totalRecords} records found
            </div>
          </ReportHeader>

          {reportData.type === 'summary' && reportData.data.summary && (
            <>
              <SummaryGrid>
                <SummaryCard>
                  <SummaryValue>{reportData.data.summary.totalAssessments}</SummaryValue>
                  <SummaryLabel>Total Assessments</SummaryLabel>
                </SummaryCard>
                <SummaryCard>
                  <SummaryValue>{(reportData.data.summary.averageReadmissionProbability ?? reportData.data.summary.averageScore).toFixed(1)}%</SummaryValue>
                  <SummaryLabel>Average Readmission Probability</SummaryLabel>
                </SummaryCard>
                <SummaryCard>
                  <SummaryValue>{reportData.data.summary.lowRisk}</SummaryValue>
                  <SummaryLabel>Low Risk (0-4)</SummaryLabel>
                </SummaryCard>
                <SummaryCard>
                  <SummaryValue>{reportData.data.summary.moderateRisk}</SummaryValue>
                  <SummaryLabel>Moderate Risk (5-9)</SummaryLabel>
                </SummaryCard>
                <SummaryCard>
                  <SummaryValue>{reportData.data.summary.highRisk}</SummaryValue>
                  <SummaryLabel>High Risk (10-19)</SummaryLabel>
                </SummaryCard>
              </SummaryGrid>

              {reportData.data.facilityBreakdown && reportData.data.facilityBreakdown.length > 0 && (
                <BreakdownSection>
                  <BreakdownTitle>
                    <Building size={16} />
                    Facility Breakdown
                  </BreakdownTitle>
                  <BreakdownTable>
                    <thead>
                      <tr>
                        <TableHeader>Facility</TableHeader>
                        <TableHeader>Assessments</TableHeader>
                        <TableHeader>Avg Readmission Probability</TableHeader>
                        <TableHeader>Low Risk</TableHeader>
                        <TableHeader>Moderate Risk</TableHeader>
                        <TableHeader>High Risk</TableHeader>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.data.facilityBreakdown.map((facility: any, index: number) => (
                        <TableRow key={index}>
                          <TableCell>{facility._id}</TableCell>
                          <TableCell>{facility.count}</TableCell>
                          <TableCell>{(facility.averageReadmissionProbability ?? facility.averageScore).toFixed(1)}%</TableCell>
                          <TableCell>{facility.lowRisk}</TableCell>
                          <TableCell>{facility.moderateRisk}</TableCell>
                          <TableCell>{facility.highRisk}</TableCell>
                        </TableRow>
                      ))}
                    </tbody>
                  </BreakdownTable>
                </BreakdownSection>
              )}

              {reportData.data.doctorBreakdown && reportData.data.doctorBreakdown.length > 0 && (
                <BreakdownSection>
                  <BreakdownTitle>
                    <User size={16} />
                    Health Care Provider Breakdown
                  </BreakdownTitle>
                  <BreakdownTable>
                    <thead>
                      <tr>
                        <TableHeader>Health Care Provider</TableHeader>
                        <TableHeader>Assessments</TableHeader>
                        <TableHeader>Avg Readmission Probability</TableHeader>
                        <TableHeader>Low Risk</TableHeader>
                        <TableHeader>Moderate Risk</TableHeader>
                        <TableHeader>High Risk</TableHeader>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.data.doctorBreakdown.map((doctor: any, index: number) => (
                        <TableRow key={index}>
                          <TableCell>{doctor._id}</TableCell>
                          <TableCell>{doctor.count}</TableCell>
                          <TableCell>{(doctor.averageReadmissionProbability ?? doctor.averageScore).toFixed(1)}%</TableCell>
                          <TableCell>{doctor.lowRisk}</TableCell>
                          <TableCell>{doctor.moderateRisk}</TableCell>
                          <TableCell>{doctor.highRisk}</TableCell>
                        </TableRow>
                      ))}
                    </tbody>
                  </BreakdownTable>
                </BreakdownSection>
              )}
            </>
          )}

          {reportData.type === 'detailed' && (
            <div>
              <p>Detailed report with {reportData.totalRecords} individual assessment records.</p>
              <p>Use the download buttons above to export the detailed data.</p>
            </div>
          )}
        </ReportCard>
      )}

      {!reportData && !loading && (
        <EmptyState>
          <FileText size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
          <h3>No Report Generated</h3>
          <p>Configure your filters and click "Generate Report" to view assessment data.</p>
        </EmptyState>
      )}
    </ReportsContainer>
  );
};

export default Reports;
